import React, { useCallback, useEffect, useState } from 'react';
import { Add } from '@mui/icons-material';
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PermissionsGroupRole, SchemaCatalogResponse } from '../../../API';
import { useSchemasAPI } from '../../../hooks/API/Config/useSchemasAPI';
import { useLocales } from '../../../hooks';
import { useRecoilValue } from 'recoil';
import { withCurrentUser } from '../../../state/auth';
import Button from '../../shared/Button';
import SchemaForm from './SchemaForm';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: theme.spacing(4)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4)
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(8)
  },
  emptyState: {
    textAlign: 'center',
    paddingTop: theme.spacing(8),
    color: theme.palette.text.secondary
  }
}));

function SchemasList(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const schemasAPI = useSchemasAPI();
  const currentUser = useRecoilValue(withCurrentUser);
  const canManageSchemas =
    currentUser?.permissionsGroups?.some(
      (permissionsGroup) => permissionsGroup.group === 'ADMIN' && permissionsGroup.role === PermissionsGroupRole.ADMIN
    ) ||
    currentUser?.permissionsGroups?.some(
      (permissionsGroup) =>
        permissionsGroup.group === 'DYNAMIC_MODULES' && permissionsGroup.role === PermissionsGroupRole.ADMIN
    );

  const [schemas, setSchemas] = useState<SchemaCatalogResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSchema, setEditingSchema] = useState<SchemaCatalogResponse | undefined>(undefined);

  const fetchSchemas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await schemasAPI.getAll();
      setSchemas(data.body);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  const openCreate = () => {
    setEditingSchema(undefined);
    setFormOpen(true);
  };

  const openEdit = (schema: SchemaCatalogResponse) => {
    setEditingSchema(schema);
    setFormOpen(true);
  };

  const onFormClose = (saved: boolean) => {
    setFormOpen(false);
    if (saved) {
      fetchSchemas();
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6">{t('schemas.title')}</Typography>
        <Button endIcon={<Add />} onClick={openCreate} disabled={!canManageSchemas}>
          {t('schemas.add_schema')}
        </Button>
      </div>

      {loading && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}

      {!loading && schemas.length === 0 && (
        <div className={classes.emptyState}>
          <Typography variant="body1">{t('schemas.empty_state')}</Typography>
        </div>
      )}

      {!loading && schemas.length > 0 && (
        <Box sx={{ overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('schemas.column_name')}</TableCell>
                <TableCell>{t('schemas.column_fields')}</TableCell>
                <TableCell>{t('schemas.column_updated')}</TableCell>
                <TableCell>{t('schemas.column_updated_by')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schemas.map((schema) => (
                <TableRow key={schema.id} className={classes.row} onClick={() => openEdit(schema)}>
                  <TableCell>{schema.name}</TableCell>
                  <TableCell>{Object.keys(schema.fields).length}</TableCell>
                  <TableCell>{formatDate(schema.updatedDate)}</TableCell>
                  <TableCell>{schema.updatedBy ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <SchemaForm open={formOpen} schema={editingSchema} onClose={onFormClose} />
    </div>
  );
}

export default SchemasList;
